import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { reportsApi } from '../../../api/client';
import RangeBar from './components/RangeBar';
import ReportTable from './components/ReportTable';
import useRangeState from './components/useRangeState';
import FiltersBar from './components/FiltersBar';
import ExportButtons from './components/ExportButtons';
import HelpButton from '../../help/HelpButton';

function KPI({ label, value, sub, accent }) {
  return (
    <div className="rounded-thin border border-surface-border dark:border-surface-dark-border p-3 bg-surface-panel dark:bg-surface-dark-panel">
      <div className="text-xs uppercase text-surface-muted tracking-wider">{label}</div>
      <div className="text-2xl font-semibold" style={{ color: accent || undefined }}>{value}</div>
      {sub && <div className="text-xs text-surface-muted mt-1">{sub}</div>}
    </div>
  );
}

const REASON_LABELS = {
  no_screen_row:       'No screen recording',
  screen_unlinked:     'Screen recorded but not linked',
  screen_failed_empty: 'Screen recording failed (empty)',
};

function reasonPill(reason) {
  const label = REASON_LABELS[reason] || reason;
  const color =
    reason === 'no_screen_row'       ? { bg: '#FEE2E2', fg: '#991B1B' } :
    reason === 'screen_unlinked'     ? { bg: '#FEF3C7', fg: '#92400E' } :
    reason === 'screen_failed_empty' ? { bg: '#FFE4E6', fg: '#9F1239' } :
                                       { bg: '#E5E7EB', fg: '#374151' };
  return (
    <span
      className="px-2 py-0.5 rounded-full text-xs font-medium"
      style={{ background: color.bg, color: color.fg }}
    >
      {label}
    </span>
  );
}

export default function CoverageGaps() {
  const [range, setRange, apiParams] = useRangeState('today');
  const [filters, setFilters] = useState({});
  const merged = { ...apiParams, ...filters };

  const { data, isLoading, isError, error, refetch, isFetching } = useQuery({
    queryKey: ['reports', 'coverage_gaps', merged],
    queryFn: () => reportsApi.coverageGaps(merged),
    keepPreviousData: true,
    refetchInterval: 60_000, // refresh every 60s so QA sees gaps close in near-real-time
  });

  const summary = data?.summary || {};
  const gapRows = data?.gap_rows || [];
  const cov = summary.coverage_pct;
  const covAccent =
    cov == null ? undefined :
    cov >= 99.5 ? '#16A34A' :
    cov >= 95   ? '#CA8A04' :
                  '#DC2626';

  const fmtTs = (v) => {
    if (!v) return '—';
    try { return new Date(v).toLocaleString(); } catch { return v; }
  };

  return (
    <div>
      <RangeBar value={range} onChange={setRange} />
      <FiltersBar value={filters} onChange={setFilters} show={['agent']} />

      <div className="flex items-center justify-between mb-2">
        <h2 className="text-base font-semibold">{data?.title || 'Screen coverage gaps'}</h2>
        <div className="flex items-center gap-2">
          <span className="text-xs text-surface-muted">{data?.subtitle}</span>
          <HelpButton moduleKey="reports" />
          <ExportButtons report="coverage-gaps" params={merged} disabled={isLoading} />
          <button onClick={() => refetch()}
            className="px-2 py-1 text-xs rounded-thin border border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle dark:hover:bg-surface-dark-subtle">
            ↻ {isFetching ? '…' : 'Refresh'}
          </button>
        </div>
      </div>

      <p className="text-xs text-surface-muted mb-3 max-w-3xl">
        Every voice call handled by an agent with <strong>screen recording enabled</strong> should
        have a matching screen recording. This report lists any that don't, with the reason.
        Calls newer than {Math.round((summary.grace_sec || 900) / 60)} minutes are excluded
        (still moving through the recording pipeline), so a call here is a genuine gap that
        needs attention. Auto-refreshes every 60s.
      </p>

      {isError && (
        <div className="text-sm text-red-600 p-3 rounded-thin border border-red-300/40 mb-3">
          {error?.detail || error?.message || 'Failed to load report.'}
        </div>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        <KPI
          label="Coverage"
          value={cov == null ? '—' : `${cov}%`}
          accent={covAccent}
          sub={cov != null && cov >= 99.5 ? 'All calls covered ✅' : 'Screen-enabled agents'}
        />
        <KPI label="Expected" value={summary.expected ?? '—'} sub="Voice calls (enabled agents)" />
        <KPI label="Covered" value={summary.covered ?? '—'} sub="With screen recording" />
        <KPI
          label="Gaps"
          value={summary.gaps ?? '—'}
          accent={summary.gaps > 0 ? '#DC2626' : '#16A34A'}
          sub={summary.gaps > 0 ? 'Need attention' : 'None 🎉'}
        />
      </div>

      {isLoading ? (
        <div className="text-sm text-surface-muted p-3">Loading…</div>
      ) : (
        <ReportTable
          headers={[
            'started_at', 'call_id', 'agent_extension', 'agent_name',
            'direction', 'caller_id', 'queue_id', 'duration_sec', 'reason',
          ]}
          rows={gapRows}
          columnLabels={{
            started_at: 'Call time',
            call_id: 'Call ID',
            agent_extension: 'Agent ext',
            agent_name: 'Agent',
            direction: 'Dir',
            caller_id: 'Other party',
            queue_id: 'Queue',
            duration_sec: 'Dur (s)',
            reason: 'Reason',
          }}
          formatters={{
            started_at: (v) => fmtTs(v),
            duration_sec: (v) => (v == null ? '—' : Math.round(v)),
            reason: (v) => reasonPill(v),
          }}
          emptyMessage="No coverage gaps for this range — every eligible call has a screen recording. 🎉"
        />
      )}
    </div>
  );
}
